import { useState, useCallback } from "react";

export interface HistoryItem {
  id: string;
  title: string;
  slug: string;
  category?: string;
  image?: string;
  date?: string;
  readAt: string;
}

const STORAGE_KEY = "bloxida_reading_history";
const MAX_HISTORY = 15;

export function useReadingHistory() {
  const [history, setHistory] = useState<HistoryItem[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Sync to localStorage
  const saveHistory = useCallback((items: HistoryItem[]) => {
    setHistory(items);
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch {
        // storage quota exceeded or disabled
      }
    }
  }, []);

  // Record article visit with 2-second engagement timer
  const recordArticleVisit = useCallback(
    (article: {
      id: string;
      title: string;
      slug: string;
      category?: string;
      image?: string;
      date?: string;
    }) => {
      if (typeof window === "undefined" || !article.id) return;

      const timer = setTimeout(() => {
        setHistory((prev) => {
          const filtered = prev.filter((item) => item.id !== article.id);
          const newItem: HistoryItem = {
            id: article.id,
            title: article.title,
            slug: article.slug,
            category: article.category,
            image: article.image,
            date: article.date,
            readAt: new Date().toISOString(),
          };
          const updated = [newItem, ...filtered].slice(0, MAX_HISTORY);
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          } catch {
            // ignore
          }
          return updated;
        });
      }, 2000); // User specified exactly 2s

      return () => clearTimeout(timer);
    },
    [],
  );

  const removeItem = useCallback(
    (id: string) => {
      const updated = history.filter((item) => item.id !== id);
      saveHistory(updated);
    },
    [history, saveHistory],
  );

  const clearHistory = useCallback(() => {
    saveHistory([]);
  }, [saveHistory]);

  return {
    history,
    recordArticleVisit,
    removeItem,
    clearHistory,
  };
}
